import { useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  useTheme,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Remove as RemoveIcon } from '@mui/icons-material';
import {
  BudgetEditItem,
  BudgetItemOption,
} from '@models/drawRequests/budgetEditItem';
import FormattedCurrency from '@helpers/formattedCurrency';
import { useApp } from '@context/AppContext';
import { colors } from '@theme/colors';

interface BudgetEditTableProps {
  items: BudgetEditItem[];
  budgetItemOptions: BudgetItemOption[];
  onItemChange: (
    index: number,
    field: keyof BudgetEditItem,
    value: any
  ) => void;
  onRemoveItem: (index: number) => void;
  onAddItem: () => void;
  onValidationChange?: (isValid: boolean) => void;
}

export default function BudgetEditTable({
  items,
  budgetItemOptions,
  onItemChange,
  onRemoveItem,
  onAddItem,
  onValidationChange,
}: BudgetEditTableProps) {
  const theme = useTheme();
  const { isTabletScreen } = useApp();
  const isFormValid = items.every(
    item =>
      item.budgetItem &&
      item.budgetItem.trim() !== '' &&
      item.requestAmount &&
      item.requestAmount > 0
  );

  const hasExceedingAmounts = items.some(
    item => (item.requestAmount || 0) > (item.remainingAmount || 0)
  );

  useEffect(() => {
    onValidationChange?.(isFormValid);
  }, [isFormValid, onValidationChange]);

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      onRemoveItem(index);
    }
  };

  const renderTableHeader = () => (
    <TableHead>
      <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            border: 'none',
            width: '250px',
            padding: '4px 8px',
          }}
        >
          Budget Item
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            border: 'none',
            width: '170px',
            padding: '4px 8px',
          }}
        >
          Remaining Budget
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            border: 'none',
            width: '150px',
            padding: '4px 8px',
            textAlign: 'left',
          }}
        >
          Request
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            border: 'none',
            width: '150px',
            padding: '4px 8px',
          }}
        >
          % Complete
        </TableCell>
        <TableCell
          component="th"
          scope="col"
          sx={{ width: 60, border: 'none', padding: '4px 8px' }}
          aria-label="Actions"
        ></TableCell>
      </TableRow>
    </TableHead>
  );

  const renderBudgetItemCell = (item: BudgetEditItem, index: number) => {
    const selectedBudgetItemIds = items
      .map((it, idx) => (idx !== index && it.budgetItem ? it.budgetItem : null))
      .filter((id): id is string => id !== null && id !== '');

    const availableOptions = budgetItemOptions.filter(
      option =>
        !selectedBudgetItemIds.includes(option.value) ||
        option.value === item.budgetItem
    );

    return (
      <TableCell sx={{ border: 'none', width: '250px', padding: '4px 8px' }}>
        <FormControl fullWidth size="small">
          <Select
            value={item.budgetItem || ''}
            onChange={e => onItemChange(index, 'budgetItem', e.target.value)}
            displayEmpty
            aria-label={`Select budget item for row ${index + 1}`}
            title={
              budgetItemOptions.find(option => option.value === item.budgetItem)
                ?.label || ''
            }
            sx={{
              backgroundColor: theme.palette.background.paper,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.grey[400],
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'cyan.500',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'cyan.500',
              },
              '& .MuiSelect-select': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            <MenuItem value="" disabled>
              <em
                style={{
                  color: theme.palette.text.disabled,
                  fontStyle: 'italic',
                }}
                aria-hidden="true"
              >
                Budget item - Remaining Budget
              </em>
            </MenuItem>
            {availableOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
    );
  };

  const renderRemainingAmountCell = (item: BudgetEditItem) => (
    <TableCell sx={{ border: 'none', width: '170px', padding: '4px 8px' }}>
      <Typography
        variant="body1"
        aria-label={`Remaining amount: ${item.remainingAmount}`}
      >
        <FormattedCurrency value={item.remainingAmount} />
      </Typography>
    </TableCell>
  );

  const renderRequestAmountCell = (item: BudgetEditItem, index: number) => {
    const isExceedingRemaining =
      (item.requestAmount || 0) > (item.remainingAmount || 0);

    return (
      <TableCell
        sx={{
          border: 'none',
          width: '150px',
          padding: '4px 8px',
          textAlign: 'left',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <TextField
            type="number"
            value={item.requestAmount || ''}
            onChange={e =>
              onItemChange(
                index,
                'requestAmount',
                e.target.value === '' ? '' : parseFloat(e.target.value) || 0
              )
            }
            variant="outlined"
            size="small"
            fullWidth
            aria-label={`Request amount for row ${index + 1}`}
            aria-describedby={
              isExceedingRemaining ? `request-amount-error-${index}` : undefined
            }
            slotProps={{
              input: {
                startAdornment: (
                  <Typography variant="body1" sx={{ mr: 1 }} aria-hidden="true">
                    $
                  </Typography>
                ),
                endAdornment: isExceedingRemaining ? (
                  <InfoIcon
                    sx={{ fill: colors.orange[500], fontSize: '18px' }}
                    aria-hidden="true"
                  />
                ) : null,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
                ...(isExceedingRemaining && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.orange[500],
                    borderWidth: '1px',
                  },
                }),
              },
              '& .MuiInputBase-input': {
                textAlign: 'left',
                padding: '8px 2px',
              },
            }}
          />
        </Box>
      </TableCell>
    );
  };

  const renderPercentCompleteCell = (item: BudgetEditItem, index: number) => (
    <TableCell sx={{ border: 'none', width: '120px', padding: '4px 8px' }}>
      <TextField
        type="number"
        value={item.percentComplete || ''}
        onChange={e =>
          onItemChange(
            index,
            'percentComplete',
            e.target.value === '' ? '' : parseFloat(e.target.value) || 0
          )
        }
        variant="outlined"
        size="small"
        fullWidth
        aria-label={`Percent complete for row ${index + 1}`}
        slotProps={{
          input: {
            endAdornment: (
              <Typography variant="body1" sx={{ ml: 1 }} aria-hidden="true">
                %
              </Typography>
            ),
          },
        }}
        sx={{
          '& .MuiInputBase-input': {
            textAlign: 'left',
            padding: '8px 12px',
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      />
    </TableCell>
  );

  const renderRemoveButtonCell = (index: number) => (
    <TableCell sx={{ border: 'none', padding: '4px 8px', textAlign: 'right' }}>
      <IconButton
        onClick={() => handleRemoveItem(index)}
        disabled={items.length === 1}
        size="small"
        aria-label={`Remove budget item ${index + 1}`}
        sx={{
          color: items.length === 1 ? 'grey.400' : 'red.500',
          backgroundColor: 'transparent',
          border: `1px solid ${items.length === 1 ? theme.palette.grey[400] : theme.palette.red[500]}`,
          borderRadius: '50%',
          width: 24,
          height: 24,
          minWidth: 24,
          '&:hover': {
            backgroundColor: items.length === 1 ? 'transparent' : 'red.100',
            borderColor: items.length === 1 ? 'grey.400' : 'red.600',
          },
          '&:disabled': {
            color: 'grey.400',
            borderColor: 'grey.400',
            cursor: 'not-allowed',
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.error.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        <RemoveIcon sx={{ fontSize: '16px' }} aria-hidden="true" />
      </IconButton>
    </TableCell>
  );
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      role="group"
      aria-label="Budget items table form"
    >
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table
          sx={{
            tableLayout: 'fixed',
            borderSpacing: '0 16px',
            borderCollapse: 'separate',
          }}
          role="table"
          aria-label="Budget items table"
        >
          {renderTableHeader()}
          <TableBody>
            {items.map((item, index) => (
              <TableRow
                key={item.id}
                sx={{
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
                aria-label={`Budget item ${index + 1} row`}
              >
                {renderBudgetItemCell(item, index)}
                {renderRemainingAmountCell(item)}
                {renderRequestAmountCell(item, index)}
                {renderPercentCompleteCell(item, index)}
                {renderRemoveButtonCell(index)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          pt: 2,
          mb: isTabletScreen ? '120px' : 0,
        }}
      >
        <Typography
          variant="body2"
          onClick={onAddItem}
          sx={{
            color: theme.palette.primary.main,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
            '&:focus': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
            },
          }}
          role="button"
          tabIndex={0}
          aria-label="Add new budget item"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onAddItem();
            }
          }}
        >
          + Add Budget Item
        </Typography>

        {hasExceedingAmounts && !isTabletScreen && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            role="alert"
            aria-live="polite"
          >
            <InfoIcon
              sx={{ fill: colors.orange[500], fontSize: '18px' }}
              aria-hidden="true"
            />
            <Typography
              variant="body3"
              sx={{
                fontStyle: 'italic',
              }}
            >
              Alert: Budget item(s) exceed the allocated amount.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
